import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Page, Layout, Card, BlockStack, Text } from '@shopify/polaris';
import { FeedDashboard } from '../components/FeedDashboard/FeedDashboard';
import { AiCrawlerSetupGuide } from '../components/FeedDashboard/AiCrawlerSetupGuide';
import { CrawlerAnalyticsDashboard } from '../components/CrawlerAnalytics/CrawlerAnalyticsDashboard';
import { ProductSchemaChecker } from '../components/FeedDashboard/ProductSchemaChecker';
import { EnableEmbedVideoModal } from '../components/EnableEmbedVideoModal';
import { PageEntrance, StaggeredList } from '../components/ui';
import { useFeedStatus } from '../hooks/useFeed';
import { checkFeedUrls } from '../services/api';
import type { UrlCheckResponse } from '../services/api';
import './FeedPage.css';

/**
 * T103 [US2]: Feed page — FeedDashboard + feed preview panel + llms.txt info + crawler analytics.
 *
 * @see contracts/feed.md
 * @see documents/phase-4-scale.md F4.3
 */
export function FeedPage() {
  const { data: feed } = useFeedStatus();
  const [videoOpen, setVideoOpen] = useState(false);
  const [shakeSignal, setShakeSignal] = useState(0);
  const autoOpenedRef = useRef(false);

  // When the user dismisses the video, nudge the Step 1 icon after 200ms
  // to draw attention back to enabling the app embed.
  const handleVideoClose = () => {
    setVideoOpen(false);
    setTimeout(() => setShakeSignal((n) => n + 1), 200);
  };

  const { data: urlCheck } = useQuery<UrlCheckResponse>({
    queryKey: ['feed-url-check'],
    queryFn: () => checkFeedUrls(),
    enabled: feed?.status === 'ACTIVE',
    staleTime: 60_000,
    retry: false,
  });

  // Auto-open the setup video once per visit when the app embed is not enabled.
  useEffect(() => {
    if (urlCheck?.embedDetected === false && !autoOpenedRef.current) {
      autoOpenedRef.current = true;
      setVideoOpen(true);
    }
  }, [urlCheck?.embedDetected]);

  return (
    <PageEntrance>
    <Page
      title="AI Product Feed"
      subtitle="Share your AI-optimised product catalog with AI platforms"
      backAction={{ content: 'Dashboard', url: '/' }}
    >
      <BlockStack gap="400">
        {/* Hero CTA — sells the AI feed feature */}
        <div className="z-hero">
          <div className="z-hero-content">
            <h2 className="z-hero-title">📡 Make your products visible to every AI engine</h2>
            <p className="z-hero-subtitle">
              Generate a structured product feed that AI crawlers like ChatGPT, Perplexity, and Google AI Overviews can discover and recommend.
            </p>
          </div>
        </div>
      <Layout>
        <Layout.Section>
          <BlockStack gap="800">
            <CrawlerAnalyticsDashboard />
            <FeedDashboard onPlayEmbedVideo={() => setVideoOpen(true)} />
          </BlockStack>
        </Layout.Section>

        <Layout.Section variant="oneThird">
          <StaggeredList>
            {/* AI Crawler Setup Guide — reads real feed URLs */}
            <AiCrawlerSetupGuide
              onPlayEmbedVideo={() => setVideoOpen(true)}
              shakeSignal={shakeSignal}
            />

            {/* JSON-LD Schema Conflict Checker */}
            <ProductSchemaChecker />

            {/* Feed Preview Info */}
            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingSm">Feed Format</Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Your feed is a paginated JSON file with 100 products per page.
                  Each product includes AI summaries, schema.org markup,
                  identifiers, and Zelium quality scores.
                </Text>
              </BlockStack>
            </Card>

            {/* llms.txt Info */}
            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingSm">llms.txt</Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  An llms.txt file is automatically generated alongside your feed.
                  It provides AI crawlers with metadata about your product feed,
                  including the feed URL, format, and update frequency.
                </Text>
              </BlockStack>
            </Card>

            {/* Daily Regeneration */}
            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingSm">Auto-Regeneration</Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Your feed is automatically regenerated daily to reflect the
                  latest product data, scores, and AI summaries. You can also
                  trigger manual regeneration at any time.
                </Text>
              </BlockStack>
            </Card>
          </StaggeredList>
        </Layout.Section>
      </Layout>
      </BlockStack>

      {/* App embed setup video — auto-opens when the embed is disabled,
          and reopenable from the Step 1 video icon. */}
      <EnableEmbedVideoModal open={videoOpen} onClose={handleVideoClose} />
    </Page>
    </PageEntrance>
  );
}
